import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "@/config/firebase";
import { doc, onSnapshot, collection, query, where, getDocs, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Check, Clock, Users, Sparkles, Send } from "lucide-react";

const CURRENT_SEASON = "2026-27";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper: Make authenticated API call with Firebase ID Token
async function apiCall(endpoint, method = 'GET', body = null) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Nicht eingeloggt');
  }
  
  const token = await currentUser.getIdToken();
  
  const options = {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  
  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }
  
  const response = await fetch(`${BACKEND_URL}${endpoint}`, options);
  const result = await response.json();
  
  if (!response.ok) {
    throw new Error(result.detail || `HTTP ${response.status}`);
  }
  
  return result;
}

// Phase constants - Extended for full game flow
const PHASES = {
  // PHASE A - Predictions
  LOBBY: 'LOBBY',
  IDENTITY_GUESS: 'IDENTITY_GUESS',
  IDENTITY_LOCKED: 'IDENTITY_LOCKED',
  IDENTITY_REVEAL: 'IDENTITY_REVEAL',
  PREDICTION_1_VOTING: 'PREDICTION_1_VOTING',
  PREDICTION_1_REVEAL: 'PREDICTION_1_REVEAL',
  PREDICTION_2_VOTING: 'PREDICTION_2_VOTING',
  PREDICTION_2_REVEAL: 'PREDICTION_2_REVEAL',
  USER_COMPLETE: 'USER_COMPLETE',
  PREDICTIONS_COMPLETE: 'PREDICTIONS_COMPLETE',
  
  // PHASE B - Challenge Games
  CHALLENGE_GAME_REVEAL: 'CHALLENGE_GAME_REVEAL',
  CHALLENGE_PLAYER_DRAW: 'CHALLENGE_PLAYER_DRAW',
  CHALLENGE_BETTING_OPEN: 'CHALLENGE_BETTING_OPEN',
  CHALLENGE_BETTING_LOCKED: 'CHALLENGE_BETTING_LOCKED',
  CHALLENGE_PLAYING: 'CHALLENGE_PLAYING',
  CHALLENGE_RESULT: 'CHALLENGE_RESULT',
  CHALLENGES_COMPLETE: 'CHALLENGES_COMPLETE',
  
  // Final
  SESSION_COMPLETE: 'SESSION_COMPLETE',
};

export default function PredictionsLive() {
  const { user } = useAuth();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  // Data
  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [users, setUsers] = useState([]);
  const [presentations, setPresentations] = useState({});
  
  // Phase B: Challenge State
  const [currentChallengeRound, setCurrentChallengeRound] = useState(null);
  const [myPosition, setMyPosition] = useState(null);
  const [wallet, setWallet] = useState(null);
  
  // User state
  const [myGuess, setMyGuess] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [hasJoined, setHasJoined] = useState(false);
  
  // Form state
  const [guessInput, setGuessInput] = useState('');
  const [voteInput, setVoteInput] = useState('');
  const [betAmount, setBetAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // UI state
  const [countdown, setCountdown] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load static data
  useEffect(() => {
    const loadData = async () => {
      const [usersSnap, presSnap, predSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(query(collection(db, "prediction_presentations"), where("seasonId", "==", CURRENT_SEASON))),
        getDocs(query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))),
      ]);
      
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      const presData = {};
      presSnap.docs.forEach(d => {
        const data = d.data();
        presData[data.userId] = { id: d.id, ...data };
      });
      setPresentations(presData);
      
      setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Subscribe to predictions for realtime updates (teamProbability, quote after finalize)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON)),
      (snap) => {
        setPredictions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    );
    
    return () => unsub();
  }, []);

  // Subscribe to session
  useEffect(() => {
    if (!sessionId) return;
    
    const unsub = onSnapshot(doc(db, "prediction_sessions", sessionId), (snap) => {
      if (snap.exists()) {
        setSession({ id: snap.id, ...snap.data() });
      }
    });
    
    return () => unsub();
  }, [sessionId]);

  // Subscribe to participants
  useEffect(() => {
    if (!sessionId) return;
    
    const unsub = onSnapshot(
      query(collection(db, "prediction_session_participants"), where("sessionId", "==", sessionId)),
      (snap) => {
        const parts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setParticipants(parts);
        
        // Check if current user has joined (support both old oduid and new userId)
        if (user && parts.some(p => p.userId === user.uid || p.oduid === user.uid)) {
          setHasJoined(true);
        }
      }
    );
    
    return () => unsub();
  }, [sessionId, user]);

  // Subscribe to current challenge round (Phase B)
  // Uses Firestore onSnapshot with API fallback on permission errors
  useEffect(() => {
    if (!session?.currentChallengeRoundId) {
      setCurrentChallengeRound(null);
      return;
    }
    
    const roundId = session.currentChallengeRoundId;
    let pollInterval = null;
    let usePolling = false;
    
    // API fallback polling function
    const pollRoundData = async () => {
      try {
        const result = await apiCall(`/api/challenges/round/${roundId}`);
        if (result && result.id) {
          setCurrentChallengeRound(result);
        }
      } catch (err) {
        console.error('[PredictionsLive] API poll error:', err);
      }
    };
    
    const unsub = onSnapshot(
      doc(db, "challenge_rounds", roundId),
      (snap) => {
        if (snap.exists()) {
          setCurrentChallengeRound({ id: snap.id, ...snap.data() });
          // Stop polling if active
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        } else {
          pollRoundData();
        }
      },
      (error) => {
        console.error('[PredictionsLive] Challenge round subscription error:', error);
        if (!usePolling) {
          usePolling = true;
          pollRoundData();
          pollInterval = setInterval(pollRoundData, 2000);
        }
      }
    );
    
    return () => {
      unsub();
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [session?.currentChallengeRoundId]);

  // Subscribe to wallet
  useEffect(() => {
    if (!user) return;
    
    const unsub = onSnapshot(
      doc(db, "prediction_wallets", user.uid),
      (snap) => {
        if (snap.exists()) {
          setWallet(snap.data());
        }
      }
    );
    
    return () => unsub();
  }, [user]);

  // Load my position for current challenge
  useEffect(() => {
    if (!session?.currentChallengeRoundId || !user) {
      setMyPosition(null);
      return;
    }
    
    const loadPosition = async () => {
      try {
        const pos = await apiCall(`/api/challenges/my-position/${sessionId}/${session.currentChallengeRoundId}`);
        setMyPosition(pos);
      } catch (err) {
        console.error('Failed to load position:', err);
        setMyPosition(null);
      }
    };
    
    loadPosition();
    
    // Reload when challenge round changes
  }, [session?.currentChallengeRoundId, user, sessionId]);

  // Subscribe to my guess for current round
  useEffect(() => {
    if (!session?.currentUserId || !sessionId || !user) return;
    
    const docId = `${sessionId}_${session.currentUserId}_${user.uid}`;
    const unsub = onSnapshot(doc(db, "prediction_identity_guesses", docId), (snap) => {
      if (snap.exists()) {
        setMyGuess(snap.data());
      } else {
        setMyGuess(null);
      }
    });
    
    return () => unsub();
  }, [session?.currentUserId, sessionId, user]);

  // Subscribe to my vote for current prediction
  useEffect(() => {
    if (!session?.currentPredictionId || !sessionId || !user) return;
    
    const docId = `${sessionId}_${session.currentPredictionId}_${user.uid}`;
    const unsub = onSnapshot(doc(db, "prediction_probability_votes", docId), (snap) => {
      if (snap.exists()) {
        setMyVote(snap.data());
      } else {
        setMyVote(null);
      }
    });
    
    return () => unsub();
  }, [session?.currentPredictionId, sessionId, user]);

  // Reset inputs when phase changes
  useEffect(() => {
    setGuessInput('');
    setVoteInput('');
  }, [session?.phase, session?.currentUserId, session?.currentPredictionId]);

  // Countdown timer
  useEffect(() => {
    if (!session?.phaseEndsAt) {
      setCountdown(null);
      return;
    }
    
    const updateCountdown = () => {
      const endsAt = session.phaseEndsAt.toDate?.() || new Date(session.phaseEndsAt);
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setCountdown(remaining);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 100);
    
    return () => clearInterval(interval);
  }, [session?.phaseEndsAt]);

  // Get current prediction
  const currentPrediction = useMemo(() => {
    if (!session?.currentPredictionId) return null;
    return predictions.find(p => p.id === session.currentPredictionId);
  }, [session?.currentPredictionId, predictions]);

  // Get current user data
  const currentUserData = useMemo(() => {
    if (!session?.currentUserId) return null;
    const u = users.find(u => u.id === session.currentUserId);
    const pres = presentations[session.currentUserId];
    return { user: u, presentation: pres };
  }, [session?.currentUserId, users, presentations]);

  // Check if I'm the current user (can't guess/vote on myself)
  const isMyTurn = session?.currentUserId === user?.uid;
  const isPredictionOwner = currentPrediction?.ownerUserId === user?.uid;

  // Join session
  const joinSession = async () => {
    if (!user || !sessionId || hasJoined) return;
    setSubmitting(true);
    
    try {
      const docId = `${sessionId}_${user.uid}`;
      await setDoc(doc(db, "prediction_session_participants", docId), {
        sessionId,
        userId: user.uid,  // Canonical field (NO oduid in new docs)
        displayName: user.displayName || user.email?.split('@')[0] || 'Unknown',
        joinedAt: serverTimestamp(),
      });
      setHasJoined(true);
    } catch (err) {
      console.error("Join error:", err);
      if (err.code === 'permission-denied') {
        alert('Du bist bereits dabei oder Session ist nicht zugänglich');
      } else {
        alert('Fehler beim Beitreten');
      }
    }
    setSubmitting(false);
  };

  // Submit guess
  const submitGuess = async () => {
    if (!user || !sessionId || !session?.currentUserId || myGuess) return;
    if (!guessInput.trim()) return;
    
    // SELF-GUESS IS NOW ALLOWED! (User can guess on their own round)
    
    setSubmitting(true);
    
    try {
      const docId = `${sessionId}_${session.currentUserId}_${user.uid}`;
      await setDoc(doc(db, "prediction_identity_guesses", docId), {
        sessionId,
        roundUserId: session.currentUserId,
        voterUserId: user.uid,  // Canonical field (NO oduid in new docs)
        answer: guessInput.trim(),
        submittedAt: serverTimestamp(),
      });
      setGuessInput('');
    } catch (err) {
      console.error("Guess error:", err);
      if (err.code === 'permission-denied') {
        alert('Du hast bereits getippt oder kannst hier nicht abstimmen');
      } else {
        alert('Fehler beim Absenden');
      }
    }
    setSubmitting(false);
  };

  // Submit vote - Uses backend endpoint for auto-finalization
  const submitVote = async () => {
    if (!user || !sessionId || !session?.currentPredictionId || myVote) return;
    
    // STRICT 0-100 INTEGER VALIDATION
    const trimmedInput = voteInput.trim();
    
    // Check if input is empty
    if (!trimmedInput) {
      alert('Bitte eine Zahl eingeben');
      return;
    }
    
    // Check if it's a valid integer (no decimals, no text)
    if (!/^-?\d+$/.test(trimmedInput)) {
      alert('Nur ganze Zahlen erlaubt (keine Dezimalzahlen)');
      return;
    }
    
    const probability = parseInt(trimmedInput, 10);
    
    // STRICT: 0 to 100 INCLUSIVE (0 is valid!)
    if (probability < 0 || probability > 100) {
      alert('Bitte eine Zahl zwischen 0 und 100 eingeben');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Use backend endpoint for auto-finalization logic
      const result = await apiCall('/api/predictions/submit-probability-vote', 'POST', {
        session_id: sessionId,
        prediction_id: session.currentPredictionId,
        probability,
      });
      
      if (result.ok) {
        setVoteInput('');
        console.log(`Vote submitted: ${probability}% | ${result.voteCount}/${result.expectedVotes} votes`);
        
        if (result.finalized) {
          console.log(`Voting auto-finalized! Quote: x${result.quote}`);
        }
      }
    } catch (err) {
      console.error("Vote error:", err);
      if (err.message.includes('bereits abgestimmt') || err.message.includes('already')) {
        alert('Du hast bereits abgestimmt');
      } else if (err.message.includes('eigene Prediction') || err.message.includes('own prediction')) {
        alert('Du kannst nicht auf deine eigene Prediction abstimmen');
      } else {
        alert(err.message || 'Fehler beim Absenden');
      }
    }
    setSubmitting(false);
  };

  // Place challenge bet - with idempotency requestId
  const placeBet = async (side) => {
    if (!user || !session?.currentChallengeRoundId || !betAmount) return;
    
    const amount = parseInt(betAmount, 10);
    if (isNaN(amount) || amount < 1) {
      alert('Bitte einen gültigen Betrag eingeben');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Generate unique request ID for idempotency
      const requestId = `${user.uid}_${session.currentChallengeRoundId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const result = await apiCall('/api/challenges/place-bet', 'POST', {
        session_id: sessionId,
        round_id: session.currentChallengeRoundId,
        side,
        amount,
        request_id: requestId,
      });
      
      if (result.ok) {
        setBetAmount('');
        // Reload position
        const pos = await apiCall(`/api/challenges/my-position/${sessionId}/${session.currentChallengeRoundId}`);
        setMyPosition(pos);
      }
    } catch (err) {
      console.error("Bet error:", err);
      alert(err.message || 'Fehler beim Platzieren der Wette');
    }
    setSubmitting(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0608] text-rose-50 flex items-center justify-center p-4">
        <p>Bitte einloggen</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0608] text-rose-50 flex items-center justify-center">
        <p>Laden...</p>
      </div>
    );
  }

  const phase = session?.phase || PHASES.LOBBY;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0608] to-[#12090c] text-rose-50">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c]/80 backdrop-blur-lg border-b border-white/5 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/predictions')} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-semibold text-sm flex items-center gap-2">
                <Sparkles size={14} className="text-amber-400" />
                {session?.name || 'Prediction Game'}
              </h1>
              <p className="text-xs text-rose-200/50">
                {participants.length} Teilnehmer
              </p>
            </div>
          </div>
          
          {/* Right side: countdown */}
          <div className="flex items-center gap-3">
            {countdown !== null && countdown > 0 && (
              <div className="flex items-center gap-2 bg-rose-600/30 px-3 py-1 rounded-full" data-testid="player-countdown">
                <Clock size={14} />
                <span className="text-lg font-mono font-bold">{countdown}</span>
              </div>
            )}
          </div>
        </div>

        {/* Persistent, prominent coin bar — always visible while playing (esp. on phones) */}
        {hasJoined && wallet && (
          <div
            className="mt-2 flex items-stretch gap-1 rounded-xl bg-gradient-to-r from-amber-500/25 to-amber-600/10 border border-amber-400/30 px-2 py-2"
            data-testid="player-wallet-badge"
          >
            <div className="flex-1 text-center px-1">
              <p className="text-[10px] text-amber-300/80 uppercase tracking-wide leading-none">Guthaben</p>
              <p className="text-2xl sm:text-xl font-black text-amber-200 leading-tight" data-testid="player-balance">{wallet.balance || 0} 🪙</p>
            </div>
            <div className="flex-1 text-center px-1 border-l border-white/10">
              <p className="text-[10px] text-green-300/80 uppercase tracking-wide leading-none">Frei</p>
              <p className="text-2xl sm:text-xl font-black text-green-300 leading-tight" data-testid="player-available">{(wallet.balance || 0) - (wallet.reserved || 0)}</p>
            </div>
            {(wallet.reserved || 0) > 0 && (
              <div className="flex-1 text-center px-1 border-l border-white/10">
                <p className="text-[10px] text-rose-300/70 uppercase tracking-wide leading-none">Reserviert</p>
                <p className="text-2xl sm:text-xl font-black text-rose-300 leading-tight" data-testid="player-reserved">{wallet.reserved}</p>
              </div>
            )}
          </div>
        )}
      </header>

      <div className="p-4 space-y-4">
        
        {/* NOT JOINED - Join Button */}
        {!hasJoined && (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <Sparkles size={48} className="text-amber-400 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Season Predictions</h2>
            <p className="text-rose-200/60 mb-8 text-center">
              {session?.name}
            </p>
            <Button 
              onClick={joinSession} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 text-lg px-8 py-6"
              data-testid="join-session-btn"
            >
              Jetzt beitreten
            </Button>
          </div>
        )}

        {/* LOBBY Phase */}
        {hasJoined && phase === PHASES.LOBBY && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="animate-pulse mb-4">
              <Users size={48} className="text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Warte auf Start...</h2>
            <p className="text-rose-200/60">
              {participants.length} Teilnehmer verbunden
            </p>
            <p className="text-xs text-rose-200/40 mt-4">
              Der Host startet das Spiel gleich
            </p>
          </div>
        )}

        {/* IDENTITY_GUESS Phase */}
        {hasJoined && (phase === PHASES.IDENTITY_GUESS || phase === PHASES.IDENTITY_LOCKED) && (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            {myGuess ? (
              // Already guessed
              <div className="text-center">
                <Check size={48} className="text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Antwort gespeichert</h2>
                <p className="text-lg text-amber-400">"{myGuess.answer}"</p>
                {(() => {
                  const raw = session?.phaseEndsAt?.toDate?.() || (session?.phaseEndsAt ? new Date(session.phaseEndsAt) : null);
                  const stale = raw && (Date.now() - raw.getTime() > 10 * 60 * 1000);
                  return stale ? (
                    <div className="mt-4 space-y-3">
                      <p className="text-sm text-rose-300/80">
                        Die Live-Runde wird gerade nicht moderiert.<br />Du kannst in Ruhe im Wett-Markt setzen.
                      </p>
                      <Button onClick={() => navigate('/predictions')} className="bg-green-600 hover:bg-green-700" data-testid="live-exit-to-market">
                        Zum Wett-Markt
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-rose-200/50 mt-4">Warte auf Reveal...</p>
                      {isMyTurn && (
                        <p className="text-xs text-amber-400/60 mt-2">(Das bist übrigens du! 😎)</p>
                      )}
                      <button onClick={() => navigate('/predictions')} className="text-xs text-rose-200/40 hover:text-rose-200/70 underline mt-6" data-testid="live-back-link">
                        Verlassen & zum Wett-Markt
                      </button>
                    </>
                  );
                })()}
              </div>
            ) : phase === PHASES.IDENTITY_LOCKED ? (
              // Locked - too late
              <div className="text-center">
                <Clock size={48} className="text-rose-200/30 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Zeit abgelaufen</h2>
                <p className="text-rose-200/60">
                  Du hast keine Antwort abgegeben
                </p>
              </div>
            ) : (
              // Can guess - INCLUDING SELF-GUESS!
              <div className="w-full max-w-md">
                <h2 className="text-3xl font-black text-center mb-8">
                  WER IST DAS?
                </h2>
                {isMyTurn && (
                  <p className="text-sm text-amber-400 text-center mb-4 bg-amber-500/10 rounded-lg p-2">
                    🎭 Das bist du! Du darfst trotzdem raten.
                  </p>
                )}
                <div className="space-y-4">
                  <Input
                    value={guessInput}
                    onChange={(e) => setGuessInput(e.target.value)}
                    placeholder="Name eingeben..."
                    className="bg-white/10 border-white/20 text-lg py-6 text-center"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && submitGuess()}
                    data-testid="guess-input"
                  />
                  <Button 
                    onClick={submitGuess}
                    disabled={submitting || !guessInput.trim()}
                    className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
                    data-testid="guess-submit-btn"
                  >
                    <Send size={18} className="mr-2" />
                    ABSENDEN
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* IDENTITY_REVEAL Phase */}
        {hasJoined && phase === PHASES.IDENTITY_REVEAL && currentUserData && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <p className="text-xl text-rose-200/60 mb-2">IT'S...</p>
            <h2 className="text-4xl font-black text-white mb-4">
              {currentUserData.user?.display_name || currentUserData.user?.name}
            </h2>
            
            {myGuess && (
              <div className={`mt-4 px-4 py-2 rounded-lg ${
                // Simple check - would need proper alias matching
                'bg-white/10'
              }`}>
                <p className="text-sm text-rose-200/60">Deine Antwort:</p>
                <p className="text-lg">{myGuess.answer}</p>
              </div>
            )}
          </div>
        )}

        {/* PREDICTION VOTING Phase */}
        {hasJoined && (phase === PHASES.PREDICTION_1_VOTING || phase === PHASES.PREDICTION_2_VOTING) && currentPrediction && (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            {isPredictionOwner ? (
              // This is my prediction - cannot vote
              <div className="text-center">
                <div className="text-6xl mb-4">📝</div>
                <h2 className="text-xl font-bold mb-2 text-amber-400">DEINE PREDICTION</h2>
                <div className="bg-white/5 rounded-xl p-4 max-w-md mx-auto mb-4">
                  <p className="text-lg">"{currentPrediction.text}"</p>
                </div>
                <p className="text-rose-200/60 px-4">
                  Die anderen schätzen jetzt ein, wie wahrscheinlich das ist.
                </p>
                {/* Vote progress for owner */}
                <div className="mt-6 text-white/50">
                  Warte auf alle Stimmen...
                </div>
              </div>
            ) : myVote ? (
              // Already voted
              <div className="text-center">
                <Check size={48} className="text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Stimme gespeichert</h2>
                <p className="text-4xl font-bold text-amber-400">{myVote.probability}%</p>
                <p className="text-sm text-rose-200/50 mt-4">
                  Warte bis alle abgestimmt haben...
                </p>
              </div>
            ) : (
              // Can vote - NO TIME LIMIT!
              <div className="w-full max-w-md">
                {/* Prediction type badge */}
                <div className="flex justify-center mb-4">
                  <span className={`px-4 py-1 rounded-full text-sm font-medium ${
                    currentPrediction.type === 'on_court'
                      ? 'bg-blue-500/30 text-blue-300'
                      : 'bg-purple-500/30 text-purple-300'
                  }`}>
                    {currentPrediction.type === 'on_court' ? '🏀 ON COURT' : '🍺 OFF COURT'}
                  </span>
                </div>
                
                {/* Prediction text */}
                <div className="bg-white/5 rounded-xl p-4 mb-6">
                  <p className="text-center text-lg">
                    "{currentPrediction.text}"
                  </p>
                  <p className="text-center text-sm text-rose-200/50 mt-2">
                    — {currentPrediction.ownerDisplayName}
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-center mb-4">
                  WIE WAHRSCHEINLICH?
                </h3>
                
                <div className="space-y-4">
                  <div className="relative">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max="100"
                      step="1"
                      value={voteInput}
                      onChange={(e) => setVoteInput(e.target.value)}
                      placeholder="0-100"
                      className="bg-white/10 border-white/20 text-4xl font-bold py-8 text-center pr-12"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && submitVote()}
                      data-testid="vote-input"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl text-rose-200/50">
                      %
                    </span>
                  </div>
                  <p className="text-xs text-rose-200/40 text-center">
                    Ganze Zahl von 0 bis 100 (0 = unmöglich, 100 = sicher)
                  </p>
                  <Button 
                    onClick={submitVote}
                    disabled={submitting || !voteInput}
                    className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
                    data-testid="vote-submit-btn"
                  >
                    <Send size={18} className="mr-2" />
                    ABGEBEN
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PREDICTION REVEAL Phase */}
        {hasJoined && (phase === PHASES.PREDICTION_1_REVEAL || phase === PHASES.PREDICTION_2_REVEAL) && currentPrediction && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <p className="text-xl text-rose-200/60 mb-2">TEAM GLAUBT</p>
            <p className="text-6xl font-black text-white mb-4">
              {currentPrediction.teamProbability?.toFixed(1)}%
            </p>
            <p className="text-xl text-amber-400 mb-2">QUOTE</p>
            <p className="text-5xl font-black text-amber-400">
              x{currentPrediction.quote?.toFixed(2)}
            </p>
            
            {myVote && (
              <div className="mt-8 bg-white/10 rounded-xl px-6 py-3">
                <p className="text-sm text-rose-200/60">Deine Stimme</p>
                <p className="text-2xl font-bold">{myVote.probability}%</p>
              </div>
            )}
          </div>
        )}

        {/* USER_COMPLETE Phase */}
        {hasJoined && phase === PHASES.USER_COMPLETE && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold mb-2">User abgeschlossen</h2>
            <p className="text-rose-200/60">
              Gleich geht's weiter...
            </p>
          </div>
        )}

        {/* PREDICTIONS_COMPLETE Phase - Phase A Done */}
        {hasJoined && phase === PHASES.PREDICTIONS_COMPLETE && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="text-6xl mb-4">🎯</div>
            <h2 className="text-3xl font-black mb-4 text-green-400">Phase A Complete!</h2>
            <p className="text-rose-200/60 mb-4">
              Alle Predictions haben ihre Quote
            </p>
            <p className="text-sm text-amber-400 animate-pulse">
              Challenge Games starten gleich...
            </p>
          </div>
        )}

        {/* ==================== PHASE B: CHALLENGE GAMES ==================== */}
        
        {/* Challenge Lobby */}
        {hasJoined && session?.phase === 'CHALLENGE_LOBBY' && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="text-6xl mb-4">🎮</div>
            <h2 className="text-2xl font-black mb-4 text-amber-400">Challenge Games</h2>
            <p className="text-rose-200/60">
              Warte auf die erste Challenge...
            </p>
            {wallet && (
              <div className="mt-6 bg-amber-900/30 px-6 py-3 rounded-xl">
                <p className="text-xs text-amber-400 uppercase">Dein Guthaben</p>
                <p className="text-3xl font-black">{wallet.balance || 0} 🪙</p>
              </div>
            )}
          </div>
        )}

        {/* Challenge Betting Open */}
        {hasJoined && currentChallengeRound?.phase === 'BETTING_OPEN' && (
          <div className="flex flex-col min-h-[60vh] p-4">
            {/* Challenge Info */}
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4 mb-4">
              <p className="text-xs text-purple-400 uppercase">Challenge #{currentChallengeRound.roundNumber}</p>
              <h2 className="text-xl font-bold mt-1">{currentChallengeRound.challengeTitle}</h2>
              <p className="text-sm text-rose-200/60 mt-2">{currentChallengeRound.challengeDescription}</p>
            </div>
            
            {/* Challenger */}
            <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-4 mb-4 text-center">
              <p className="text-xs text-amber-400 uppercase">Challenger</p>
              <p className="text-2xl font-black">{currentChallengeRound.challengerDisplayName}</p>
            </div>
            
            {/* Check if user is challenger */}
            {currentChallengeRound.challengerUserId === user?.uid ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center p-6 bg-amber-900/20 rounded-xl">
                  <p className="text-amber-400 text-lg">Du bist der Challenger!</p>
                  <p className="text-sm text-rose-200/60 mt-2">Du kannst nicht auf deine eigene Challenge wetten.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Live Odds */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-900/30 border border-green-500/30 rounded-xl p-4 text-center">
                    <p className="text-xs text-green-400 uppercase">JA</p>
                    <p className="text-4xl font-black text-green-400">x{currentChallengeRound.yesOdds?.toFixed(2)}</p>
                    <p className="text-xs text-rose-200/40">{currentChallengeRound.yesStake || 0} Coins</p>
                  </div>
                  <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4 text-center">
                    <p className="text-xs text-red-400 uppercase">NEIN</p>
                    <p className="text-4xl font-black text-red-400">x{currentChallengeRound.noOdds?.toFixed(2)}</p>
                    <p className="text-xs text-rose-200/40">{currentChallengeRound.noStake || 0} Coins</p>
                  </div>
                </div>
                
                {/* My Position with Bet Tickets */}
                {myPosition?.hasPosition && (
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-3 mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Deine Wette: <span className="font-bold">{myPosition.side}</span></span>
                      <span>{myPosition.totalStake} / {myPosition.maxChallengeStake} Coins</span>
                    </div>
                    
                    {/* Individual Bet Tickets */}
                    {myPosition.bets?.length > 0 && (
                      <div className="border-t border-blue-500/20 pt-2 mt-2 space-y-1">
                        <p className="text-xs text-blue-400 uppercase">Deine Tickets</p>
                        {myPosition.bets.map((bet, idx) => (
                          <div key={idx} className="flex justify-between text-xs text-rose-200/60">
                            <span>{bet.amount} {myPosition.side} @ x{bet.oddsAtPlacement?.toFixed(2)}</span>
                            <span className="text-green-400">→ {bet.potentialPayout?.toFixed(0)} Coins</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Wallet Info - Balance / Reserved / Available */}
                <div className="bg-amber-900/20 border border-amber-500/20 rounded-xl p-3 mb-4">
                  <p className="text-xs text-amber-400 uppercase mb-2">Dein Wallet</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-rose-200/40">Balance</p>
                      <p className="text-lg font-bold">{wallet?.balance || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-rose-200/40">Reserviert</p>
                      <p className="text-lg font-bold text-amber-400">{wallet?.reserved || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-rose-200/40">Verfügbar</p>
                      <p className="text-lg font-bold text-green-400">{(wallet?.balance || 0) - (wallet?.reserved || 0)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Bet Amount Input with Potential Payout */}
                <div className="space-y-3">
                  {(() => {
                    const roundLimit = myPosition?.maxChallengeStake || Math.floor((wallet?.balance || 0) * 0.5);
                    const already = myPosition?.totalStake || 0;
                    const remaining = Math.max(0, roundLimit - already);
                    return (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3" data-testid="max-stake-hint">
                        <p className="text-xs text-amber-400 uppercase text-center mb-2 font-semibold">Dein Wett-Limit diese Runde</p>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <p className="text-[10px] text-rose-200/40 uppercase">Limit (½ Guthaben)</p>
                            <p className="text-lg font-bold text-amber-300" data-testid="stake-limit-total">{roundLimit} 🪙</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-rose-200/40 uppercase">Schon gesetzt</p>
                            <p className="text-lg font-bold text-rose-200/70" data-testid="stake-already">{already} 🪙</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-rose-200/40 uppercase">Noch möglich</p>
                            <p className="text-lg font-bold text-green-400" data-testid="stake-remaining">{remaining} 🪙</p>
                          </div>
                        </div>
                        <p className="text-[11px] text-rose-200/50 text-center mt-2">
                          Du darfst pro Runde insgesamt max. die Hälfte deines Guthabens setzen – auch verteilt auf mehrere Wetten.
                        </p>
                      </div>
                    );
                  })()}
                  <Input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    placeholder="Einsatz..."
                    className="bg-white/5 border-white/10 text-center text-2xl py-6"
                    min={1}
                    max={myPosition?.maxChallengeStake ? Math.max(0, myPosition.maxChallengeStake - (myPosition.totalStake || 0)) : Math.floor((wallet?.balance || 0) * 0.5)}
                    disabled={submitting}
                    data-testid="bet-amount-input"
                  />
                  
                  {/* Potential Payout Preview */}
                  {betAmount && parseInt(betAmount) > 0 && (
                    <div className="text-center text-sm">
                      <span className="text-rose-200/60">Möglicher Gewinn: </span>
                      <span className="text-green-400 font-bold">
                        {myPosition?.side === 'YES' || !myPosition?.side 
                          ? `JA: ${(parseInt(betAmount) * (currentChallengeRound.yesOdds || 2)).toFixed(0)} Coins`
                          : ''}
                        {!myPosition?.side && ' | '}
                        {myPosition?.side === 'NO' || !myPosition?.side 
                          ? `NEIN: ${(parseInt(betAmount) * (currentChallengeRound.noOdds || 2)).toFixed(0)} Coins`
                          : ''}
                      </span>
                    </div>
                  )}
                  
                  {/* Bet Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => placeBet('YES')}
                      disabled={submitting || !betAmount || (myPosition?.side && myPosition.side !== 'YES')}
                      className="bg-green-600 hover:bg-green-700 py-6 text-lg disabled:opacity-50"
                      data-testid="bet-yes-btn"
                    >
                      JA wetten
                    </Button>
                    <Button
                      onClick={() => placeBet('NO')}
                      disabled={submitting || !betAmount || (myPosition?.side && myPosition.side !== 'NO')}
                      className="bg-red-600 hover:bg-red-700 py-6 text-lg disabled:opacity-50"
                      data-testid="bet-no-btn"
                    >
                      NEIN wetten
                    </Button>
                  </div>
                  
                  {myPosition?.side && (
                    <p className="text-xs text-center text-rose-200/40">
                      Du hast auf {myPosition.side} gesetzt. Kein Wechsel möglich.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Challenge In Progress / Locked */}
        {hasJoined && (currentChallengeRound?.phase === 'BETTING_LOCKED' || currentChallengeRound?.phase === 'IN_PROGRESS') && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
            <div className="text-6xl mb-4 animate-pulse">⏳</div>
            <h2 className="text-2xl font-black mb-2 text-amber-400">Challenge läuft!</h2>
            <p className="text-lg font-bold">{currentChallengeRound.challengeTitle}</p>
            <p className="text-rose-200/60 mt-2">{currentChallengeRound.challengerDisplayName}</p>
            
            {myPosition?.hasPosition && (
              <div className="mt-6 bg-blue-900/20 border border-blue-500/30 rounded-xl px-6 py-3">
                <p className="text-xs text-blue-400 uppercase">Deine Wette</p>
                <p className="text-xl font-bold">
                  {myPosition.totalStake} Coins auf {myPosition.side}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Challenge Settled */}
        {hasJoined && currentChallengeRound?.phase === 'SETTLED' && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
            <div className={`text-8xl mb-4 ${
              currentChallengeRound.result === 'SUCCESS' ? '' : 
              currentChallengeRound.result === 'FAIL' ? '' : ''
            }`}>
              {currentChallengeRound.result === 'SUCCESS' ? '✅' :
               currentChallengeRound.result === 'FAIL' ? '❌' : '⊘'}
            </div>
            <h2 className={`text-3xl font-black mb-4 ${
              currentChallengeRound.result === 'SUCCESS' ? 'text-green-400' :
              currentChallengeRound.result === 'FAIL' ? 'text-red-400' : 'text-gray-400'
            }`}>
              {currentChallengeRound.result === 'SUCCESS' ? 'ERFOLG!' :
               currentChallengeRound.result === 'FAIL' ? 'GESCHEITERT' : 'ABGEBROCHEN'}
            </h2>
            <p className="text-lg">{currentChallengeRound.challengerDisplayName}</p>
            
            {myPosition?.hasPosition && (
              <div className={`mt-6 px-6 py-4 rounded-xl ${
                (currentChallengeRound.result === 'SUCCESS' && myPosition.side === 'YES') ||
                (currentChallengeRound.result === 'FAIL' && myPosition.side === 'NO')
                  ? 'bg-green-900/30 border border-green-500/30'
                  : currentChallengeRound.result === 'VOID'
                    ? 'bg-gray-900/30 border border-gray-500/30'
                    : 'bg-red-900/30 border border-red-500/30'
              }`}>
                <p className="text-sm">
                  {(currentChallengeRound.result === 'SUCCESS' && myPosition.side === 'YES') ||
                   (currentChallengeRound.result === 'FAIL' && myPosition.side === 'NO')
                    ? '🎉 Gewonnen!'
                    : currentChallengeRound.result === 'VOID'
                      ? '↩️ Einsatz zurück'
                      : '💸 Verloren'}
                </p>
              </div>
            )}
            
            <p className="text-sm text-rose-200/40 mt-6">
              Nächste Challenge folgt...
            </p>
          </div>
        )}

        {/* SESSION_COMPLETE Phase */}
        {hasJoined && phase === PHASES.SESSION_COMPLETE && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-black mb-4">Geschafft!</h2>
            <p className="text-rose-200/60 mb-8">
              Alle Predictions haben ihre Quote
            </p>
            <Button 
              onClick={() => navigate('/predictions')}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Zum Prediction Market
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
